import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import { use } from 'react'
import styled from 'styled-components'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { busesSelector } from '@/scopes/prices/components/selectors'
import { BusCard } from '@/scopes/prices/components/TransportSelector/BusSelector/BusCard'
import { InboundTitle, OutboundTitle } from '@/scopes/prices/components/TransportSelector/BusSelector/Headers'
import { CollapseByHeight } from '@/scopes/prices/components/TransportSelector/CollapseByHeight'
import { Column } from '@/scopes/prices/components/TransportSelector/Column'
import { createBusComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const Wrapper = styled.div`
  display: flex;
  width: 100%;

  ${Column}:first-child {
    margin-right: ${space('primitives_space_16')};
  }
`

const BusSelectorTitle = styled(Typography)`
  display: block;
`

const BusSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_24')};
  padding: ${space('primitives_space_24')};
  border: 2px solid ${tok('primitives_color_neutral_100')};
  border-radius: 12px;
`

export function BusSelectorDesktop() {
  const intl = useIntl()
  const { setOutboundBusId, setInboundBusId } = use(PricesActionsContext)
  const { outboundBuses, inboundBuses, selectedOutboundBusId, selectedInboundBusId } = usePricesSelector(busesSelector)
  const { sendEvent } = useAnalytics()

  if (isEmpty(outboundBuses) || isEmpty(inboundBuses))
    return null

  function handleCollapseToggle(result: string) {
    sendEvent(createBusComponentInteractionEvent({ type: 'outbound', result }))
  }

  const selectOutboundBus = (id: string) => {
    setOutboundBusId(id)
  }

  const selectInboundBus = (id: string) => {
    setInboundBusId(id)
  }

  const content = (
    <Wrapper id="BusSelector">
      <Column value={selectedOutboundBusId as string}>
        <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
          <OutboundTitle />
          {outboundBuses.map(item => (
            <BusCard
              key={item.id}
              cityName={item.departureCityName}
              stationName={item.departureStationName}
              date={item.departureDate}
              {...item}
              isSelected={selectedOutboundBusId === item.id}
              setSelected={selectOutboundBus}
            />
          ))}
        </SpaceBetween>
      </Column>
      <Column value={selectedInboundBusId as string}>
        <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
          <InboundTitle />
          {inboundBuses.map(item => (
            <BusCard
              key={item.id}
              cityName={item.arrivalCityName}
              stationName={item.arrivalStationName}
              isSelected={selectedInboundBusId === item.id}
              {...item}
              setSelected={selectInboundBus}
            />
          ))}
        </SpaceBetween>
      </Column>
    </Wrapper>
  )
  return (
    <BusSelectorContainer>
      <BusSelectorTitle variant="display-f-regular">
        {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_title' })}
      </BusSelectorTitle>
      {outboundBuses.length < 3
        ? (
            <>{content}</>
          )
        : (
            <CollapseByHeight
              openText={intl.formatMessage({ id: 'prices_transport_selector_bus-selector-show-less' })}
              closeText={intl.formatMessage({ id: 'prices_transport_selector_bus-selector-show-more' })}
              height="370px"
              onClick={handleCollapseToggle}
            >
              {content}
            </CollapseByHeight>
          )}
    </BusSelectorContainer>
  )
}
